import { debounce } from 'lodash';
import { memo, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { CustomIcon } from '~components/index';
import InputLabel from '../InputLabel/InputLabel';
import '../inputs.scss';

interface Props extends CommonInputProps {
  type?: 'text' | 'number' | 'date' | 'datetime-local' | 'password';
  min?: string;
  step?: string;
  iconName?: IconName;
  value?: string | number;
  defaultValue?: string | number;
  max?: string;
  hookOnChange?: (value: string) => void;
  onInputChange?: (value: string) => void;
  debounceTime?: number;
}

const Input = ({
  type = 'text',
  name,
  label,
  placeholder,
  step,
  labelHelper,
  iconName,
  value,
  defaultValue,
  disabled = false,
  required = false,
  readOnly = false,
  design = 'col-12',
  hookError,
  min,
  max,
  onInputChange = () => {},
  hookOnChange = () => {},
  onFocus = () => {},
  onBlur = () => {},
  debounceTime = 300
}: Props) => {
  const { t } = useTranslation();
  const isControlled = value !== undefined;
  const onInputChangeRef = useRef(onInputChange);

  useEffect(() => {
    onInputChangeRef.current = onInputChange;
  }, [onInputChange]);

  const debouncedOnInputChange = useMemo(
    () =>
      debounce(
        (nextValue: string) => onInputChangeRef.current(nextValue),
        debounceTime
      ),
    [debounceTime]
  );

  useEffect(
    () => () => debouncedOnInputChange.cancel(),
    [debouncedOnInputChange]
  );

  return (
    <div className={`customInput ${design}`}>
      {label && (
        <InputLabel
          helper={labelHelper}
          name={name}
          text={label}
          required={required}
        />
      )}
      <div
        className={`customInput_container ${
          disabled ? 'customInput_disabled' : ''
        }`}
      >
        {iconName && (
          <CustomIcon
            className="customInput_icon"
            name={iconName}
            color="var(--green-light)"
          />
        )}
        <input
          type={type}
          id={name}
          min={min}
          max={max}
          step={step}
          placeholder={placeholder && t(placeholder)}
          disabled={disabled}
          readOnly={readOnly}
          value={isControlled ? String(value ?? '') : undefined}
          defaultValue={!isControlled ? defaultValue : undefined}
          onChange={(e) => {
            const nextValue = e.target.value;
            hookOnChange(nextValue);
            debouncedOnInputChange(nextValue);
          }}
          onWheel={(e) => {
            (e.target as HTMLElement).blur();
            e.stopPropagation();
          }}
          onFocus={onFocus}
          onBlur={onBlur}
        />
      </div>
      {hookError?.message && (
        <p className="customInput_error">{t(hookError?.message)}</p>
      )}
    </div>
  );
};

const MemoizeInput = memo(Input);
export default MemoizeInput;
